import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type ProfileHeaderState = {
  title?: ReactNode;
  onBack?: (() => void) | null;
  backButton?: ReactNode;
  isBackButtonHidden?: boolean;
};

type ProfileHeaderActions = {
  setHeader: (state: ProfileHeaderState) => void;
  resetHeader: () => void;
};

const defaultHeader: ProfileHeaderState = {};

const ProfileHeaderStateContext =
  createContext<ProfileHeaderState>(defaultHeader);

const ProfileHeaderActionsContext =
  createContext<ProfileHeaderActions | null>(null);

type ProfileHeaderProviderProps = {
  children: ReactNode;
};

export const ProfileHeaderProvider = ({
  children,
}: ProfileHeaderProviderProps) => {
  const [header, setHeaderState] = useState<ProfileHeaderState>(defaultHeader);

  const setHeader = useCallback((state: ProfileHeaderState) => {
    setHeaderState(state);
  }, []);

  const resetHeader = useCallback(() => {
    setHeaderState(defaultHeader);
  }, []);

  const actions = useMemo(
    () => ({
      setHeader,
      resetHeader,
    }),
    [setHeader, resetHeader],
  );

  return (
    <ProfileHeaderActionsContext.Provider value={actions}>
      <ProfileHeaderStateContext.Provider value={header}>
        {children}
      </ProfileHeaderStateContext.Provider>
    </ProfileHeaderActionsContext.Provider>
  );
};

export const useProfileHeaderState = () => {
  return useContext(ProfileHeaderStateContext);
};

export const useOptionalProfileHeaderActions = () => {
  return useContext(ProfileHeaderActionsContext);
};